import { Routes } from '@angular/router';
import { canActivateLayout } from './layout/auth/auth.guard';
import { userContextResolver } from './common/user/user-context.resolver';
import { testingSessionResolver } from './testing/tests/testing-session.resolver';

export const profileRoutePath = 'profile';
export const accountRoutePath = 'account';
export const homeRoutePath = 'home';
export const testingRoutePath = 'testing';
export const newTestRoutePath = 'new';
export const draftRoutePath = 'draft';
export const leaderboardRoutePath = 'leaderboard';
export const loginRoutePath = 'login';
export const forgotPasswordRoutePath = 'forgot';
export const resetPasswordRoutePath = 'reset';
export const loginEmailRoutePath = 'email';
export const resultsRoutePath = 'results';
export const signupRoutePath = 'sign-up';
export const welcomeRoutePath = 'welcome';
export const toolsRoutePath = 'tools';
export const scheduleRoutePath = 'schedule';
export const teamsRoutePath = 'teams';
export const authRoutePath = 'auth';
export const oauthCallbackRoutePath = 'callback';

// Universal/App Link handling must never claim this path: the OAuth code can only be exchanged by
// the browser that started the flow, because that is where the PKCE verifier was stored.
export const oauthCallbackPathname = `/${authRoutePath}/${oauthCallbackRoutePath}`;

// The sections the side menu's destinations are registered under
export const menuSectionRoutePaths = [
  homeRoutePath,
  testingRoutePath,
  leaderboardRoutePath,
] as const;

type NonUserRoutePath =
  | typeof loginRoutePath
  | typeof signupRoutePath
  | typeof testingRoutePath
  | typeof resetPasswordRoutePath;

type LoginChildRoutePath =
  | typeof loginEmailRoutePath
  | typeof forgotPasswordRoutePath;

export function getLoginRoutePath(redirect?: string) {
  if (!redirect) {
    return getNonUserRoutePath(loginRoutePath);
  }

  return `${getNonUserRoutePath(loginRoutePath)}?redirect=${encodeURIComponent(redirect)}`;
}

export function getLoginChildRoutePath(routePath: LoginChildRoutePath) {
  return `${getNonUserRoutePath(loginRoutePath)}/${routePath}`;
}

export function getNonUserRoutePath(routePath: NonUserRoutePath) {
  return `/${routePath}`;
}

/**
 * Reduces a user URL to `/user/:userId/{home|testing|leaderboard}` (query and
 * fragment stripped) so menu destinations are appended under a registered
 * section. Non-user URLs are returned as-is, minus query/fragment.
 */
export function getMenuBasePath(url: string) {
  const [path] = url.split(/[?#]/);
  const segments = path.split('/').filter(Boolean);

  if (segments[0] !== 'user' || !segments[1]) {
    return path;
  }

  const section = (menuSectionRoutePaths as readonly string[]).includes(
    segments[2],
  )
    ? segments[2]
    : homeRoutePath;

  return `/user/${segments[1]}/${section}`;
}

export function getOAuthCallbackUrl(origin: string, redirect?: string | null) {
  const url = new URL(oauthCallbackPathname, origin);
  // `redirect` is a decoded in-app URL that may itself carry `?`/`&` (invite codes, filters);
  // it has to be a single percent-encoded parameter or the provider return trip truncates it.
  if (redirect) {
    url.searchParams.set('redirect', redirect);
  }
  return url.toString();
}

export function getUserRoutePath(
  userId: string,
  routePath?: NonUserRoutePath | string,
) {
  if (!routePath) {
    return `/user/${userId}`;
  }
  return `/user/${userId}/${routePath}`;
}

export const routes: Routes = [
  // Auth redirect component will handle authentication checks
  {
    path: '',
    loadComponent: () =>
      import('./common/auth/auth-redirect.component').then(
        (m) => m.AuthRedirectComponent,
      ),
  },
  {
    path: `${authRoutePath}/${oauthCallbackRoutePath}`,
    loadComponent: () =>
      import('./common/auth/auth-redirect.component').then(
        (m) => m.AuthRedirectComponent,
      ),
  },
  {
    path: resetPasswordRoutePath,
    title: 'Reset Password | Olympia Fitness',
    loadComponent: () =>
      import('./login/reset-password/reset-password.page').then(
        (m) => m.ResetPasswordPage,
      ),
  },
  {
    path: loginRoutePath,
    children: [
      {
        path: '',
        title: 'Login | Olympia Fitness',
        loadComponent: () =>
          import('./login/login.page').then((m) => m.LoginPage),
      },
      {
        path: loginEmailRoutePath,
        title: 'Login with Email | Olympia Fitness',
        loadComponent: () =>
          import('./login/login-with-email/login-with-email.page').then(
            (m) => m.LoginWithEmailPage,
          ),
      },
      {
        path: forgotPasswordRoutePath,
        title: 'Forgot Password | Olympia Fitness',
        loadComponent: () =>
          import('./login/forgot-password/forgot-password.page').then(
            (m) => m.ForgotPasswordPage,
          ),
      },
    ],
  },
  {
    path: signupRoutePath,
    title: 'Sign Up | Olympia Fitness',
    loadComponent: () =>
      import('./sign-up/sign-up.page').then((m) => m.SignUpPage),
  },
  {
    path: `user/:userId/${welcomeRoutePath}`,
    title: 'Welcome | Olympia Fitness',
    loadComponent: () =>
      import('./welcome/welcome.page').then((m) => m.WelcomePage),
  },

  // Protected routes with userId
  {
    path: 'user/:userId',
    canActivate: [canActivateLayout],
    resolve: {
      userContext: userContextResolver,
    },
    loadComponent: () =>
      import('./layout/layout.component').then((m) => m.LayoutComponent),
    children: [
      {
        path: '',
        redirectTo: homeRoutePath,
        pathMatch: 'full',
      },
      {
        path: 'access/:targetUserId',
        loadComponent: () =>
          import('./access/access.page').then((m) => m.AccessPage),
      },
      // TODO BG remove
      {
        path: teamsRoutePath,
        children: [
          {
            path: '',
            title: 'Add Team | Olympia Fitness',
            loadComponent: () =>
              import('./teams/team-add/team-add.page').then(
                (m) => m.TeamAddPage,
              ),
          },
          {
            path: ':teamCode',
            title: 'Team | Olympia Fitness',
            loadComponent: () =>
              import('./teams/team-view/team-view.page').then(
                (m) => m.TeamViewPage,
              ),
          },
        ],
      },
      {
        path: homeRoutePath,
        children: [
          {
            path: '',
            title: 'Home | Olympia Fitness',
            loadComponent: () =>
              import('./home/home.page').then((m) => m.HomePage),
          },
          {
            path: accountRoutePath,
            title: 'Account | Olympia Fitness',
            loadComponent: () =>
              import('./account/account.page').then((m) => m.AccountPage),
          },
          {
            path: profileRoutePath,
            title: 'Profile | Olympia Fitness',
            loadComponent: () =>
              import('./profile/profile.page').then((m) => m.ProfilePage),
          },
          {
            path: toolsRoutePath,
            title: 'Tools | Olympia Fitness',
            loadComponent: () =>
              import('./tools/tools.page').then((m) => m.ToolsPage),
          },
          {
            path: scheduleRoutePath,
            title: 'Schedule | Olympia Fitness',
            loadComponent: () =>
              import('./schedule/schedule.page').then((m) => m.SchedulePage),
          },
          {
            path: teamsRoutePath,
            children: [
              {
                path: '',
                title: 'Add Team | Olympia Fitness',
                loadComponent: () =>
                  import('./teams/team-add/team-add.page').then(
                    (m) => m.TeamAddPage,
                  ),
              },
              {
                path: ':teamCode',
                title: 'Team | Olympia Fitness',
                loadComponent: () =>
                  import('./teams/team-view/team-view.page').then(
                    (m) => m.TeamViewPage,
                  ),
              },
            ],
          },
          {
            path: resultsRoutePath,
            children: [
              {
                path: '',
                title: 'Results | Olympia Fitness',
                loadComponent: () =>
                  import('./results/results.page').then((m) => m.ResultsPage),
              },
              {
                path: ':test_group_id',
                title: 'Result Details | Olympia Fitness',
                loadComponent: () =>
                  import('./results/result-detail/result-detail.page').then(
                    (m) => m.ResultDetailPage,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: testingRoutePath,
        children: [
          {
            path: '',
            title: 'Testing | Olympia Fitness',
            loadComponent: () =>
              import('./testing/testing.page').then((m) => m.TestingPage),
          },
          {
            path: accountRoutePath,
            title: 'Account | Olympia Fitness',
            loadComponent: () =>
              import('./account/account.page').then((m) => m.AccountPage),
          },
          {
            path: profileRoutePath,
            title: 'Profile | Olympia Fitness',
            loadComponent: () =>
              import('./profile/profile.page').then((m) => m.ProfilePage),
          },
          {
            path: toolsRoutePath,
            title: 'Tools | Olympia Fitness',
            loadComponent: () =>
              import('./tools/tools.page').then((m) => m.ToolsPage),
          },
          {
            path: scheduleRoutePath,
            title: 'Schedule | Olympia Fitness',
            loadComponent: () =>
              import('./schedule/schedule.page').then((m) => m.SchedulePage),
          },
          {
            path: teamsRoutePath,
            children: [
              {
                path: '',
                title: 'Add Team | Olympia Fitness',
                loadComponent: () =>
                  import('./teams/team-add/team-add.page').then(
                    (m) => m.TeamAddPage,
                  ),
              },
              {
                path: ':teamCode',
                title: 'Team | Olympia Fitness',
                loadComponent: () =>
                  import('./teams/team-view/team-view.page').then(
                    (m) => m.TeamViewPage,
                  ),
              },
            ],
          },
          // New route for creating new test (redirects to draft)
          {
            path: newTestRoutePath,
            title: 'New Test | Olympia Fitness',
            resolve: {
              testingSession: testingSessionResolver,
            },
            loadComponent: () =>
              import('./testing/tests/tests.component').then(
                (m) => m.TestsComponent,
              ),
          },
          // New route for existing drafts
          {
            path: `${draftRoutePath}/:draftId`,
            title: 'Test Draft | Olympia Fitness',
            resolve: {
              testingSession: testingSessionResolver,
            },
            loadComponent: () =>
              import('./testing/tests/tests.component').then(
                (m) => m.TestsComponent,
              ),
          },
          {
            path: resultsRoutePath,
            children: [
              {
                path: '',
                title: 'Results | Olympia Fitness',
                loadComponent: () =>
                  import('./results/results.page').then((m) => m.ResultsPage),
              },
              {
                path: ':test_group_id',
                title: 'Result Details | Olympia Fitness',
                loadComponent: () =>
                  import('./results/result-detail/result-detail.page').then(
                    (m) => m.ResultDetailPage,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: leaderboardRoutePath,
        children: [
          {
            path: '',
            title: 'Leaderboard | Olympia Fitness',
            loadComponent: () =>
              import('./leaderboard/leaderboard.page').then(
                (m) => m.LeaderboardPage,
              ),
          },
          {
            path: accountRoutePath,
            title: 'Account | Olympia Fitness',
            loadComponent: () =>
              import('./account/account.page').then((m) => m.AccountPage),
          },
          {
            path: profileRoutePath,
            title: 'Profile | Olympia Fitness',
            loadComponent: () =>
              import('./profile/profile.page').then((m) => m.ProfilePage),
          },
          {
            path: toolsRoutePath,
            title: 'Tools | Olympia Fitness',
            loadComponent: () =>
              import('./tools/tools.page').then((m) => m.ToolsPage),
          },
          {
            path: scheduleRoutePath,
            title: 'Schedule | Olympia Fitness',
            loadComponent: () =>
              import('./schedule/schedule.page').then((m) => m.SchedulePage),
          },
          {
            path: teamsRoutePath,
            children: [
              {
                path: '',
                title: 'Add Team | Olympia Fitness',
                loadComponent: () =>
                  import('./teams/team-add/team-add.page').then(
                    (m) => m.TeamAddPage,
                  ),
              },
              {
                path: ':teamCode',
                title: 'Team | Olympia Fitness',
                loadComponent: () =>
                  import('./teams/team-view/team-view.page').then(
                    (m) => m.TeamViewPage,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: profileRoutePath,
        children: [
          {
            path: '',
            title: 'Profile | Olympia Fitness',
            loadComponent: () =>
              import('./profile/profile.page').then((m) => m.ProfilePage),
          },
        ],
      },
      {
        path: accountRoutePath,
        title: 'Account | Olympia Fitness',
        loadComponent: () =>
          import('./account/account.page').then((m) => m.AccountPage),
      },
      {
        path: toolsRoutePath,
        title: 'Tools | Olympia Fitness',
        loadComponent: () =>
          import('./tools/tools.page').then((m) => m.ToolsPage),
      },
    ],
  },
  {
    path: '**',
    title: '404 | Olympia Fitness',
    loadComponent: () => import('./404/404.page').then((m) => m.NotFoundPage),
  },
];
